import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface ReservationItem {
  id: string;
  productId: string;
  quantity: number;
  price: number;
  product: {
    name: string;
    sku: string;
  };
}

interface Reservation {
  id: string;
  trackingNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  totalAmount: number;
  totalItems: number;
  status: string;
  expiresAt: string;
  createdAt: string;
  notes?: string;
  items: ReservationItem[];
}

// History types
enum SaleHistoryAction {
  RESERVATION_CREATED = 'reservation_created',
  RESERVATION_UPDATED = 'reservation_updated',
  RESERVATION_PICKED_UP = 'reservation_picked_up',
  RESERVATION_CANCELLED = 'reservation_cancelled',
}

interface HistoryEntry {
  id: string;
  action: SaleHistoryAction;
  performedBy?: {
    firstName: string;
    lastName: string;
  };
  metadata?: {
    previousStatus?: string;
    newStatus?: string;
    reason?: string;
  };
  notes?: string;
  createdAt: string;
}

// Status configuration for history display
const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pending', color: '#ed6c02', bgColor: 'rgba(237, 108, 2, 0.1)' },
  confirmed: { label: 'Confirmed', color: '#0288d1', bgColor: 'rgba(2, 136, 209, 0.1)' },
  ready_for_pickup: { label: 'Ready for Pickup', color: '#1976d2', bgColor: 'rgba(25, 118, 210, 0.1)' },
  completed: { label: 'Completed', color: '#2e7d32', bgColor: 'rgba(46, 125, 50, 0.1)' },
  cancelled: { label: 'Cancelled', color: '#d32f2f', bgColor: 'rgba(211, 47, 47, 0.1)' },
  expired: { label: 'Expired', color: '#757575', bgColor: 'rgba(117, 117, 117, 0.1)' },
};

// Action labels for history display
const actionLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  reservation_created: { label: 'Reservation Created', icon: <EventIcon fontSize="small" />, color: '#1976d2' },
  reservation_updated: { label: 'Status Updated', icon: <HistoryIcon fontSize="small" />, color: '#0288d1' },
  reservation_picked_up: { label: 'Picked Up', icon: <InventoryOutlinedIcon fontSize="small" />, color: '#2e7d32' },
  reservation_cancelled: { label: 'Cancelled', icon: <CancelOutlinedIcon fontSize="small" />, color: '#d32f2f' },
};

export const TrackReservationPage: React.FC = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [email, setEmail] = useState('');
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!trackingNumber.trim() || !email.trim()) {
      setError('Please enter both tracking number and email');
      return;
    }

    setLoading(true);
    setError('');
    setReservation(null);
    setHistory([]);

    try {
      const response = await axios.get(
        `${API_URL}/reservations/track`,
        {
          params: {
            trackingNumber: trackingNumber.trim(),
            email: email.trim(),
          },
        }
      );

      setReservation(response.data);

      // Fetch history for the reservation
      try {
        const historyResponse = await axios.get(
          `${API_URL}/sales/reservation/${response.data.id}/history`
        );
        setHistory(historyResponse.data);
      } catch (historyErr) {
        // History is optional, don't fail the whole request
        console.warn('Failed to fetch reservation history:', historyErr);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('No reservation found with this tracking number and email. Please verify your information.');
      } else {
        setError('Failed to track reservation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'Your reservation is waiting for pickup. Please visit the store before the expiration date.';
      case 'confirmed':
        return 'Your reservation has been confirmed and is ready for pickup.';
      case 'completed':
        return 'This reservation has been completed. Thank you for your business!';
      case 'cancelled':
        return 'This reservation has been cancelled.';
      case 'expired':
        return 'This reservation has expired. Please create a new one if needed.';
      default:
        return '';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom fontWeight="bold" textAlign="center">
        Track Your Reservation
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
        Enter your tracking number and email to view your reservation details
      </Typography>

      <Paper sx={{ p: 4, mb: 4 }}>
        <form onSubmit={handleTrack}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tracking Number"
                placeholder="RES-20251118-XXXXX"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                required
                helperText="Format: RES-YYYYMMDD-XXXXX"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                helperText="The email you used when creating the reservation"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                disabled={loading}
              >
                {loading ? 'Tracking...' : 'Track Reservation'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {reservation && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Reservation {reservation.trackingNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created: {new Date(reservation.createdAt).toLocaleDateString()} at{' '}
                  {new Date(reservation.createdAt).toLocaleTimeString()}
                </Typography>
              </Box>
              <Chip
                label={reservation.status.toUpperCase()}
                color={getStatusColor(reservation.status) as any}
                size="medium"
              />
            </Box>

            <Alert severity={getStatusColor(reservation.status) as any} sx={{ mb: 3 }}>
              {getStatusMessage(reservation.status)}
            </Alert>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Customer Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {reservation.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {reservation.customerEmail}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {reservation.customerPhone}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Expires At
                </Typography>
                <Typography variant="body1" fontWeight="medium" color={
                  new Date(reservation.expiresAt) < new Date() ? 'error' : 'text.primary'
                }>
                  {new Date(reservation.expiresAt).toLocaleDateString()} at{' '}
                  {new Date(reservation.expiresAt).toLocaleTimeString()}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Reservation Items ({reservation.totalItems})
            </Typography>

            <List>
              {reservation.items.map((item) => (
                <ListItem key={item.id} sx={{ px: 0 }}>
                  <ListItemText
                    primary={item.product.name}
                    secondary={`SKU: ${item.product.sku} | Quantity: ${item.quantity}`}
                  />
                  <Typography variant="body1" fontWeight="medium">
                    {(parseFloat(item.price.toString()) * item.quantity).toFixed(2)} DZD
                  </Typography>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Total Amount</Typography>
              <Typography variant="h5" color="primary" fontWeight="bold">
                {typeof reservation.totalAmount === 'string'
                  ? parseFloat(reservation.totalAmount).toFixed(2)
                  : reservation.totalAmount.toFixed(2)}{' '}
                DZD
              </Typography>
            </Box>

            {reservation.notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body1">{reservation.notes}</Typography>
              </>
            )}

            {/* Activity History Timeline */}
            {history.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <HistoryIcon color="primary" />
                  <Typography variant="h6">Activity History</Typography>
                </Box>

                <Stack spacing={0}>
                  {history.map((entry, index) => {
                    const actionInfo = actionLabels[entry.action] || {
                      label: entry.action,
                      icon: <HistoryIcon fontSize="small" />,
                      color: '#757575',
                    };
                    const isLast = index === history.length - 1;

                    return (
                      <Box key={entry.id} display="flex" gap={2}>
                        {/* Timeline line and dot */}
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: `${actionInfo.color}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: actionInfo.color,
                            }}
                          >
                            {actionInfo.icon}
                          </Box>
                          {!isLast && (
                            <Box
                              sx={{
                                width: 2,
                                flex: 1,
                                minHeight: 40,
                                bgcolor: 'divider',
                              }}
                            />
                          )}
                        </Box>

                        {/* Content */}
                        <Box pb={isLast ? 0 : 3} flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {actionInfo.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(entry.createdAt).toLocaleString('fr-DZ', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                          {entry.metadata?.previousStatus && entry.metadata?.newStatus && (
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                              <Chip
                                label={statusConfig[entry.metadata.previousStatus]?.label || entry.metadata.previousStatus}
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 20,
                                  bgcolor: statusConfig[entry.metadata.previousStatus]?.bgColor,
                                  color: statusConfig[entry.metadata.previousStatus]?.color,
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">→</Typography>
                              <Chip
                                label={statusConfig[entry.metadata.newStatus]?.label || entry.metadata.newStatus}
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 20,
                                  bgcolor: statusConfig[entry.metadata.newStatus]?.bgColor,
                                  color: statusConfig[entry.metadata.newStatus]?.color,
                                }}
                              />
                            </Box>
                          )}
                          {entry.notes && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                fontStyle: 'italic',
                              }}
                            >
                              "{entry.notes}"
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
};
